import { Api } from '@teko-builder/utils';
import { __rest } from 'tslib';

const transformPrice = (prices) => {
    var _a, _b, _c, _d;
    const latestPrice = (_a = prices === null || prices === void 0 ? void 0 : prices[0]) === null || _a === void 0 ? void 0 : _a.latestPrice;
    const supplierRetailPrice = (_b = prices === null || prices === void 0 ? void 0 : prices[0]) === null || _b === void 0 ? void 0 : _b.supplierRetailPrice;
    const discountPercent = (_c = prices === null || prices === void 0 ? void 0 : prices[0]) === null || _c === void 0 ? void 0 : _c.discountPercent;
    const discountAmount = (_d = prices === null || prices === void 0 ? void 0 : prices[0]) === null || _d === void 0 ? void 0 : _d.discountAmount;
    return {
        latestPrice: latestPrice && +latestPrice,
        discountAmount: discountAmount && +discountAmount,
        discountPercent,
        supplierRetailPrice: supplierRetailPrice && +supplierRetailPrice,
    };
};
const getDefaultPromotions = (promotions) => {
    var _a;
    const couponProductPromotion = (_a = promotions.filter((promo) => {
        var _a;
        const isTypeCoupon = promo.type === 'coupon';
        const isApplyOnProduct = promo.applyOn === 'product';
        const hasCoupon = !!((_a = promo === null || promo === void 0 ? void 0 : promo.condition) === null || _a === void 0 ? void 0 : _a.coupon);
        return isTypeCoupon && isApplyOnProduct && hasCoupon;
    })) === null || _a === void 0 ? void 0 : _a[0];
    const defaultPromotions = promotions.filter((promo) => {
        const isTypeProduct = promo.type === 'product';
        const isApplyOnProduct = promo.applyOn === 'product';
        const isDefault = promo.isDefault;
        return isTypeProduct && isApplyOnProduct && isDefault;
    });
    return [...defaultPromotions, couponProductPromotion].filter(Boolean);
};
const getSelectionPromotions = (promotions) => {
    return promotions.filter((promo) => {
        const isTypeProduct = promo.type === 'product';
        const isApplyOnProduct = promo.applyOn === 'product';
        const isDefault = promo.isDefault;
        return isTypeProduct && isApplyOnProduct && !isDefault;
    });
};
const checkGift = (promotions) => {
    const appliedPromotion = [
        ...getDefaultPromotions(promotions),
        ...getSelectionPromotions(promotions),
    ];
    return appliedPromotion.some((el) => { var _a; return (((_a = el === null || el === void 0 ? void 0 : el.benefit) === null || _a === void 0 ? void 0 : _a.gifts) || []).length; });
};
const transformProduct = (product) => {
    const { flashSales, prices, productInfo, promotions, totalAvailable } = product;
    const { canonical, name, sku, imageUrl } = productInfo;
    return Object.assign(Object.assign({ totalAvailable }, transformPrice(prices)), { url: `/${canonical}`, name,
        sku,
        imageUrl, hasGift: checkGift(promotions), flashSale: flashSales === null || flashSales === void 0 ? void 0 : flashSales[0] });
};
const transformSearchBody = (menuItem) => {
    const searchBody = {
        sorting: {
            order: 'ORDER_BY_UNSPECIFIED',
            sort: 'SORT_BY_UNSPECIFIED',
        },
        filter: {},
    };
    if (Object.keys((menuItem === null || menuItem === void 0 ? void 0 : menuItem.sorting) || {}).length) {
        searchBody.sorting = Object.assign(Object.assign({}, searchBody.sorting), menuItem.sorting);
    }
    if (Object.keys((menuItem === null || menuItem === void 0 ? void 0 : menuItem.configs) || {}).length) {
        const _a = menuItem.configs, { price, providerIds, sellerIds, attributes, hasPromotions } = _a, rest = __rest(_a, ["price", "providerIds", "sellerIds", "attributes", "hasPromotions"]);
        searchBody.filter = rest;
        if (providerIds === null || providerIds === void 0 ? void 0 : providerIds.length) {
            searchBody.filter.providerIds = providerIds.map((el) => el.toString());
        }
        if (sellerIds === null || sellerIds === void 0 ? void 0 : sellerIds.length) {
            searchBody.filter.sellerIds = sellerIds.map((el) => el.toString());
        }
        if (attributes === null || attributes === void 0 ? void 0 : attributes.length) {
            searchBody.filter.attributes = attributes.map((el) => ({
                code: el.code,
                optionIds: (el.optionIds || []).map((o) => o.toString()),
            }));
        }
        if (price === null || price === void 0 ? void 0 : price.from) {
            searchBody.filter.priceGte = price.from.toString();
        }
        if (price === null || price === void 0 ? void 0 : price.to) {
            searchBody.filter.priceLte = price.to.toString();
        }
        if (hasPromotions) {
            searchBody.filter.hasPromotions = !!hasPromotions;
        }
    }
    return searchBody;
};
const convertMenuItemConfigToUrl = (menuItem) => {
    const configs = menuItem.configs || {};
    const sorting = menuItem.sorting || {};
    const urlObject = new URLSearchParams();
    Object.keys(sorting).forEach((k) => urlObject.append(k, sorting[k]));
    const { price, attributes } = configs, restConfig = __rest(configs, ["price", "attributes"]);
    const { from, to } = price || {};
    if (from) {
        urlObject.set('price_gte', from.toString());
    }
    if (to) {
        urlObject.set('price_lte', to.toString());
    }
    (attributes || []).forEach((attr) => {
        urlObject.set(`attributes.${attr.code}`, attr.optionIds.join(','));
    });
    Object.keys(restConfig).forEach((k) => {
        const val = restConfig[k];
        if (Array.isArray(val) && val.length > 0) {
            urlObject.set(k, val.join(','));
        }
        else if (typeof val === 'number' ||
            typeof val === 'string' ||
            typeof val === 'boolean') {
            urlObject.set(k, `${val}`);
        }
    });
    return `/search?${urlObject.toString()}`;
};

const discoveryURIMap = {
    'test-1': 'https://discovery.dev.tekoapis.net/api',
    dev: 'https://discovery.dev.tekoapis.net/api',
    stag: 'https://discovery.stag.tekoapis.net/api',
    production: 'https://discovery.tekoapis.com/api',
};
class TekoProductPlugin {
    constructor() {
        this._mockTerminals = [];
        if (!this.instance) {
            this.instance = this;
        }
        return this.instance;
    }
    set env(value) {
        this._env = value;
    }
    set terminalCode(value) {
        this._terminalCode = value;
    }
    get terminalCode() {
        return this._terminalCode;
    }
    set mockTerminals(value) {
        this._mockTerminals = value;
    }
    get mockTerminals() {
        return this._mockTerminals;
    }
    set locationCode(value) {
        this._locationCode = value;
    }
    get discoveryURI() {
        return discoveryURIMap[this._env];
    }
    async fetchProducts(terminalCode, menuItemId, size = 10, page = 1, baseMenuItem) {
        var _a, _b;
        if (!this.discoveryURI) {
            throw 'Please set discoveryURI!';
        }
        if (!menuItemId || !terminalCode) {
            return;
        }
        const client = new Api('');
        let menuItem = baseMenuItem;
        if (!menuItem) {
            menuItem = await client
                .get(`${this.discoveryURI}/v1/menu-item/${menuItemId}`)
                .then((res) => { var _a; return (_a = res.data) === null || _a === void 0 ? void 0 : _a.result; });
        }
        if (!menuItem)
            return {
                products: [],
            };
        const res = await client.post(`${this.discoveryURI}/v1/search`, Object.assign(Object.assign({}, transformSearchBody(menuItem)), { terminalCode, location: this._locationCode, pagination: {
                pageNumber: page,
                itemsPerPage: size,
            } }));
        const products = ((_a = res.data.result) === null || _a === void 0 ? void 0 : _a.products) || [];
        const totalPages = ((_b = res.data.pagination) === null || _b === void 0 ? void 0 : _b.totalPages) || 0;
        return {
            products,
            hasNextPage: totalPages > page,
            menuItem,
        };
    }
    cookProducts(products) {
        return products.map((el) => transformProduct(el));
    }
}
const pluginTekoProduct = new TekoProductPlugin();
const getMenuItemById = async (id) => {
    const client = new Api(pluginTekoProduct.discoveryURI);
    return client.get(`/v1/menu-item/${id}`).then((res) => res.data);
};
const searchProduct = async (searchBody) => {
    const client = new Api(pluginTekoProduct.discoveryURI);
    return client.post('/v1/search', searchBody).then((res) => res.data);
};
const fetchProducts = async (env, options, baseMenuItem) => {
    var _a, _b;
    const { menuItemId, terminalCode, locationCode, size = 10, page = 1, } = options;
    const discoveryURI = discoveryURIMap[env];
    if (!discoveryURI) {
        throw 'Please set discoveryURI!';
    }
    if (!menuItemId || !terminalCode) {
        return;
    }
    const client = new Api('');
    let menuItem = baseMenuItem;
    if (!menuItem) {
        menuItem = await client
            .get(`${discoveryURI}/v1/menu-item/${menuItemId}`)
            .then((res) => { var _a; return (_a = res.data) === null || _a === void 0 ? void 0 : _a.result; });
    }
    if (!menuItem)
        return {
            products: [],
        };
    const res = await client.post(`${discoveryURI}/v1/search`, Object.assign(Object.assign({}, transformSearchBody(menuItem)), { terminalCode, location: locationCode, pagination: {
            pageNumber: page,
            itemsPerPage: size,
        } }));
    const products = ((_a = res.data.result) === null || _a === void 0 ? void 0 : _a.products) || [];
    const totalPages = ((_b = res.data.pagination) === null || _b === void 0 ? void 0 : _b.totalPages) || 0;
    return {
        products,
        hasNextPage: totalPages > page,
        menuItem,
    };
};
const fetchMenuItemById = async (env, id) => {
    const client = new Api(discoveryURIMap[env]);
    return client.get(`/v1/menu-item/${id}`).then((res) => res.data);
};
const cookProducts = (products) => {
    return products.map((el) => transformProduct(el));
};

export { convertMenuItemConfigToUrl, cookProducts, fetchMenuItemById, fetchProducts, getMenuItemById, pluginTekoProduct, searchProduct, transformSearchBody };
